import { useEffect, useRef } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Escape for layers drawn over the page (side sheet, zoomed image): only the top one closes, and
 * nothing under it — a form dialog listening for Escape — hears the key. One window listener in the
 * capture phase, so it runs before every other handler.
 */
const layers: { current: () => void }[] = [];
function onLayerKey(e: KeyboardEvent) {
  if (e.key !== 'Escape' || !layers.length) return;
  e.preventDefault();
  e.stopPropagation();
  layers[layers.length - 1].current();
}

export function useEscapeLayer(active: boolean, close: () => void) {
  const ref = useRef(close);
  ref.current = close;
  useEffect(() => {
    if (!active) return;
    const entry = { current: () => ref.current() };
    if (!layers.length) window.addEventListener('keydown', onLayerKey, true);
    layers.push(entry);
    return () => {
      layers.splice(layers.indexOf(entry), 1);
      if (!layers.length) window.removeEventListener('keydown', onLayerKey, true);
    };
  }, [active]);
}

/** Tab stays inside `box` while active; focus moves in on open and back to the opener on close. */
export function useFocusTrap(box: React.RefObject<HTMLElement>, active: boolean) {
  useEffect(() => {
    if (!active) return;
    const opener = document.activeElement as HTMLElement | null;
    const panel = box.current;
    (panel?.querySelector<HTMLElement>('[data-autofocus]') ?? panel)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !box.current) return;
      const all = Array.from(box.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      const visible = all.filter((el) => el.getClientRects().length > 0);
      const items = visible.length ? visible : all; // jsdom has no layout
      if (!items.length) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const inside = box.current.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === first || !inside || document.activeElement === box.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      if (opener && opener !== document.body && opener.isConnected) opener.focus();
    };
  }, [active, box]);
}
